import React, { useState } from 'react';
import { FaRobot, FaSpinner, FaCheck, FaTimes, FaMagic } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Admin token wrapper — the interceptor on `api` sends 'token',
// but our admin session uses 'adminToken'. Override here.
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
});
const adminApi = {
  post: (url, data) => api.post(url, data, { headers: authHeader() }),
};

export default function AICreateUserModal({ onClose, onCreated }) {
  const [stage, setStage] = useState('input');
  const [description, setDescription] = useState('');
  const [draft, setDraft] = useState(null);
  const [missing, setMissing] = useState([]);
  const [error, setError] = useState(null);

  const extract = async () => {
    if (!description.trim()) return;
    setStage('extracting');
    setError(null);
    try {
      // ⭐ uses adminApi, not api
      const res = await adminApi.post('/ai/draft-user', { description });
      if (!res.data.success) throw new Error(res.data.message);
      setDraft(res.data.draft);
      setMissing(res.data.missing || []);
      setStage('review');
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
      setStage('input');
      toast.error('AI extraction failed');
    }
  };

  const create = async () => {
    setStage('saving');
    try {
      // ⭐ uses adminApi, not api
      const res = await adminApi.post('/ai/create-user', { draft });
      if (!res.data.success) throw new Error(res.data.message);
      toast.success('User created successfully!');
      onCreated?.(res.data.userId);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
      setStage('review');
    }
  };

  const updateField = (section, field, value) => {
    setDraft(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [field]: value },
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaRobot className="text-indigo-600" />
            AI Create User
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <FaTimes />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {stage === 'input' && (
            <>
              <p className="text-gray-600 mb-3 text-sm">
                Paste a description — from WhatsApp, email, or type freely. AI will extract all fields.
              </p>
              <textarea
                rows={8}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={`Example:\nSumit Jha, male, born 1996-05-12, from Kathmandu Nepal, unmarried, Chhetri caste, engineer earning 125000, phone 9801234567, email sumit@example.com, father Ram Jha businessman, mother Sita housewife, height 5ft8, weight 70kg, education Bachelors`}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm resize-none"
              />
              {error && (
                <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  ⚠️ {error}
                </div>
              )}
            </>
          )}

          {stage === 'extracting' && (
            <div className="text-center py-16">
              <FaSpinner className="text-5xl text-indigo-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">AI is extracting fields…</p>
              <p className="text-xs text-gray-400 mt-2">This takes 5-10 seconds with Qwen3 4B</p>
            </div>
          )}

          {stage === 'review' && draft && (
            <div className="space-y-6">
              {missing.length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  ⚠️ Missing: <strong>{missing.join(', ')}</strong>. Please fill them below.
                </div>
              )}

              <Section title="Personal">
                <Field label="Name *" value={draft.personal?.name} onChange={v => updateField('personal', 'name', v)} highlight={!draft.personal?.name} />
                <Field label="Gender *" value={draft.personal?.gender} onChange={v => updateField('personal', 'gender', v)} highlight={!draft.personal?.gender} />
                <Field label="DOB * (YYYY-MM-DD)" value={draft.personal?.dob} onChange={v => updateField('personal', 'dob', v)} highlight={!draft.personal?.dob} />
                <Field label="Marital *" value={draft.personal?.marital} onChange={v => updateField('personal', 'marital', v)} highlight={!draft.personal?.marital} />
                <Field label="Caste" value={draft.personal?.caste} onChange={v => updateField('personal', 'caste', v)} />
                <Field label="Gotra" value={draft.personal?.gotra} onChange={v => updateField('personal', 'gotra', v)} />
                <Field label="Birth State" value={draft.personal?.birth_state} onChange={v => updateField('personal', 'birth_state', v)} />
                <Field label="Birth City" value={draft.personal?.birth_city} onChange={v => updateField('personal', 'birth_city', v)} />
                <Field label="About Me" value={draft.personal?.about_me} onChange={v => updateField('personal', 'about_me', v)} />
              </Section>

              <Section title="Contact">
                <Field label="Phone" value={draft.contact?.contact} onChange={v => updateField('contact', 'contact', v)} />
                <Field label="Email" value={draft.contact?.email} onChange={v => updateField('contact', 'email', v)} />
                <Field label="Country" value={draft.contact?.country} onChange={v => updateField('contact', 'country', v)} />
                <Field label="State" value={draft.contact?.state} onChange={v => updateField('contact', 'state', v)} />
                <Field label="District" value={draft.contact?.district} onChange={v => updateField('contact', 'district', v)} />
                <Field label="City" value={draft.contact?.city} onChange={v => updateField('contact', 'city', v)} />
              </Section>

              <Section title="Appearance">
                <Field label="Height" value={draft.appreance?.height} onChange={v => updateField('appreance', 'height', v)} />
                <Field label="Weight" value={draft.appreance?.weight} onChange={v => updateField('appreance', 'weight', v)} />
                <Field label="Skin Tone" value={draft.appreance?.skin_tone} onChange={v => updateField('appreance', 'skin_tone', v)} />
              </Section>

              <Section title="Education">
                <Field label="Education" value={draft.education?.education} onChange={v => updateField('education', 'education', v)} />
                <Field label="Occupation" value={draft.education?.occupation} onChange={v => updateField('education', 'occupation', v)} />
                <Field label="Monthly Income" value={draft.education?.monthaly_income} onChange={v => updateField('education', 'monthaly_income', v)} />
                <Field label="Annual Income" value={draft.education?.annual_income} onChange={v => updateField('education', 'annual_income', v)} />
                <Field label="Employee In" value={draft.education?.employee_in} onChange={v => updateField('education', 'employee_in', v)} />
                <Field label="Designation" value={draft.education?.designation} onChange={v => updateField('education', 'designation', v)} />
              </Section>

              <Section title="Family">
                <Field label="Father" value={draft.family?.father} onChange={v => updateField('family', 'father', v)} />
                <Field label="Father Occupation" value={draft.family?.father_occupation} onChange={v => updateField('family', 'father_occupation', v)} />
                <Field label="Mother" value={draft.family?.mother} onChange={v => updateField('family', 'mother', v)} />
                <Field label="Mother Occupation" value={draft.family?.mother_occupation} onChange={v => updateField('family', 'mother_occupation', v)} />
                <Field label="Unmarried Sisters" value={draft.family?.unmarride_sister} onChange={v => updateField('family', 'unmarride_sister', v)} />
                <Field label="Married Sisters" value={draft.family?.marride_sister} onChange={v => updateField('family', 'marride_sister', v)} />
                <Field label="Unmarried Brothers" value={draft.family?.unmarride_brother} onChange={v => updateField('family', 'unmarride_brother', v)} />
                <Field label="Married Brothers" value={draft.family?.marride_brother} onChange={v => updateField('family', 'marride_brother', v)} />
              </Section>

              <Section title="Partner Preferences">
                <Field label="Looking For" value={draft.life_partner?.looking_for} onChange={v => updateField('life_partner', 'looking_for', v)} />
                <Field label="Partner Marital" value={draft.life_partner?.marit} onChange={v => updateField('life_partner', 'marit', v)} />
                <Field label="Partner Age" value={draft.life_partner?.age} onChange={v => updateField('life_partner', 'age', v)} />
                <Field label="Partner Education" value={draft.life_partner?.partner_education} onChange={v => updateField('life_partner', 'partner_education', v)} />
                <Field label="Partner Occupation" value={draft.life_partner?.partner_occupation} onChange={v => updateField('life_partner', 'partner_occupation', v)} />
                <Field label="Location" value={draft.life_partner?.location} onChange={v => updateField('life_partner', 'location', v)} />
              </Section>
            </div>
          )}

          {stage === 'saving' && (
            <div className="text-center py-16">
              <FaSpinner className="text-5xl text-indigo-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Creating user…</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          {stage === 'input' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={extract}
                disabled={!description.trim()}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-lg font-medium flex items-center gap-2"
              >
                <FaMagic /> Extract with AI
              </button>
            </>
          )}
          {stage === 'review' && (
            <>
              <button
                onClick={() => setStage('input')}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                ← Back
              </button>
              <button
                onClick={create}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center gap-2"
              >
                <FaCheck /> Create User
              </button>
            </>
          )}
          {stage === 'saving' && (
            <button disabled className="px-5 py-2 bg-gray-300 text-white rounded-lg">
              Creating…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-semibold text-gray-800 mb-3 pb-2 border-b">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, highlight }) {
  return (
    <label className="block">
      <span className="text-xs text-gray-500 block mb-1">{label}</span>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none ${
          highlight ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}
      />
    </label>
  );
}